import { useEffect, useState } from "react";

/**
 * Chart colours, read from the same CSS custom properties that drive the rest
 * of the dashboard (declared on `body, #root` in styles.scss, overridden in the
 * `prefers-color-scheme: dark` block).
 *
 * echarts renders to a canvas, so it cannot inherit CSS. Without this, charts
 * fall back to echarts' built-in defaults (dark grey axis text on a transparent
 * background), which disappear on a dark card. Reading the variables keeps one
 * source of truth for the palette instead of a second one in JS.
 */

const FALLBACK = {
  text: "#111827",
  muted: "#6b7280",
  axis: "#cccccc",
  split: "#eeeeee",
  track: "#e9ecef",
  tooltipBg: "#ffffff",
};

const VAR_NAMES = {
  text: "--global-chart-text",
  muted: "--global-chart-muted",
  axis: "--global-chart-axis",
  split: "--global-chart-split",
  track: "--global-chart-track",
  tooltipBg: "--global-chart-tooltip-bg",
};

export function readChartTheme() {
  if (typeof window === "undefined" || !document.body) return { ...FALLBACK };

  const styles = window.getComputedStyle(document.body);
  const resolved = {};

  Object.entries(VAR_NAMES).forEach(([key, cssVar]) => {
    const value = styles.getPropertyValue(cssVar).trim();
    resolved[key] = value || FALLBACK[key];
  });

  return resolved;
}

const sameTheme = (a, b) => Object.keys(VAR_NAMES).every((k) => a[k] === b[k]);

/**
 * Re-reads the palette when the colour scheme changes, so charts repaint
 * alongside the CSS instead of keeping the colours they mounted with.
 *
 * The identity of the returned object is deliberately stable while the colours
 * are unchanged. Chart options are memoised on this value, and handing back a
 * new-but-equal object rebuilds the option and restarts echarts' draw
 * animation — which leaves the series as bare point markers with no lines.
 */
export function useChartTheme() {
  const [theme, setTheme] = useState(readChartTheme);

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () =>
      setTheme((previous) => {
        const next = readChartTheme();
        return sameTheme(previous, next) ? previous : next;
      });

    // Re-read once on mount: in dev the stylesheet is injected by JS and may
    // land after the first render. The guard above makes this free when the
    // values already match.
    update();

    if (query.addEventListener) {
      query.addEventListener("change", update);
      return () => query.removeEventListener("change", update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  return theme;
}

/**
 * Shared axis/legend/tooltip defaults for a themed chart. Spread into an
 * echarts option; per-chart options still win.
 */
export function chartDefaults(theme) {
  return {
    textStyle: { color: theme.text },
    legend: { textStyle: { color: theme.muted } },
    tooltip: {
      backgroundColor: theme.tooltipBg,
      borderColor: theme.split,
      textStyle: { color: theme.text },
    },
  };
}

/** Axis config fragment shared by category and value axes. */
export function axisDefaults(theme) {
  return {
    axisLine: { lineStyle: { color: theme.axis } },
    axisTick: { lineStyle: { color: theme.axis } },
    axisLabel: { color: theme.muted },
    splitLine: { lineStyle: { color: theme.split } },
  };
}

function themeAxis(axis, theme) {
  if (Array.isArray(axis)) return axis.map((a) => themeAxis(a, theme));
  if (!axis || typeof axis !== "object") return axis;
  return {
    ...axis,
    axisLabel: { ...(axis.axisLabel || {}), color: theme.muted },
    splitLine: {
      ...(axis.splitLine || {}),
      lineStyle: { ...((axis.splitLine || {}).lineStyle || {}), color: theme.split },
    },
  };
}

/**
 * Applies the palette to an existing echarts option.
 *
 * Only the theme-dependent chrome is overridden — axis label colour, gridline
 * colour, legend/tooltip text. Series colours and per-axis accent lines
 * (`axisLine`, used here to tint an axis to match its series) are left as the
 * chart author set them, because those carry meaning rather than theme.
 */
export function withChartTheme(option, theme) {
  if (!option || typeof option !== "object") return option;

  const themed = {
    ...option,
    textStyle: { ...(option.textStyle || {}), color: theme.text },
    legend: option.legend
      ? {
          ...option.legend,
          textStyle: { ...((option.legend || {}).textStyle || {}), color: theme.muted },
          // A scrolling legend paginates; its arrows and "1/2" counter default
          // to near-black and vanish on a dark card.
          pageTextStyle: {
            ...((option.legend || {}).pageTextStyle || {}),
            color: theme.muted,
          },
          pageIconColor: theme.muted,
          pageIconInactiveColor: theme.split,
        }
      : option.legend,
    tooltip: option.tooltip
      ? {
          ...option.tooltip,
          backgroundColor: theme.tooltipBg,
          borderColor: theme.split,
          textStyle: { ...((option.tooltip || {}).textStyle || {}), color: theme.text },
        }
      : option.tooltip,
  };

  if (option.xAxis) themed.xAxis = themeAxis(option.xAxis, theme);
  if (option.yAxis) themed.yAxis = themeAxis(option.yAxis, theme);

  return themed;
}
